import {ref } from 'vue';
import {
    spectatorHandshake,
    barHero_getSiteStatus,
    getAppsForDevice,
    getAppsForSite,
    getDefaultAppForTv
} from './cloudhub/Cheerios';
import {params, systemInfo} from "@/systemInfo.js";
import {
    getFormattedDate,
    getFormattedDateTime,
    getFormattedTime,
    handleOffline,
    handleOnline
} from "@/services/helperFunctions.js";
import {rebootDevice} from "@/services/reboot.js";
import {initializeDeviceManager} from "@/services/buzztimeRemote.js";
import {getSnFromUrl} from "@/services/serial.js"
import {
    ensureAppIds,
    fallbackAppsList,
    fallbackDefaultApp,
    initializeApps,
    initializeDefaultApp,
    processApps
} from "@/apps.js";
import {getPairingToken, getDeviceId} from './virtualDisplay.js';

// Create a reactive status message for the loading screen
export const loadingStatus = ref('');
export const loadingStep = ref(''); // Current step being executed
export const loadingSuccess = ref(true); // Whether the current step succeeded
export const startupMode = ref(null); // 'hardware', 'virtual-paired', 'pending'

// Helper function to update loading status with animation trigger
const updateLoadingStatus = (message, step, success = true) => {
    loadingStatus.value = message;
    loadingStep.value = step;
    loadingSuccess.value = success;

    // The animation will be triggered in the component by watching these values
    console.info(`Loading status: ${message} (${step})`);
};

const setSystemTime = () => {
    systemInfo.time = getFormattedDateTime();
    systemInfo.headerDate = getFormattedDateTime('t');
    setInterval(() => {
        systemInfo.time = getFormattedDateTime();
        systemInfo.headerDate = getFormattedDateTime('t');
    }, 10000);
}

const getIP = () => {
    fetch('https://api.ipify.org?format=jsonp&callback=getIP')
        .then(response => response.text())
        .then(response => {
            systemInfo.ip = response.match(/"ip":"([^"]+)"/)[1];
        })
        .catch(error => console.warn('Error fetching IP address:', error));
}

export async function startupSequence(statusCallback) {
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    setSystemTime();
    getIP();

    try {
        updateLoadingStatus('Welcome! Performing system checks...', 'init');

        const pathDecision = await determineStartupPath();
        // const pathDecision = 'hardware';

        if (pathDecision === 'virtual-unpaired') {
            // Halt sequence and signal the caller (App.vue/Router) to load Pairing.vue
            return {status: 'pairing_required'};
        }

        if (pathDecision === 'hardware') {
            updateLoadingStatus('Getting serial number...', 'serialNumber');
            await getSerialNumber();
        } else if (pathDecision === 'virtual-paired') {
            updateLoadingStatus('Virtual display authenticated', 'serialNumber');
        }

        // Resume common sequence
        updateLoadingStatus('Performing handshake...', 'handshake');
        await doHandshake();

        updateLoadingStatus('Connecting to Buzztime Remote...', 'remote');
        await doRemoteConnection();

        updateLoadingStatus('Fetching Apps...', 'apps');
        await getApps();

        updateLoadingStatus('Loading prize data...', 'prizeData');
        await getBarHeroStatus();

        updateLoadingStatus('Initialization Complete!', 'complete');

        await new Promise(resolve => setTimeout(resolve, params.startupWait * 1000));
        return {status: 'success'};
    } catch (error) {
        console.error('Startup sequence failed:', error);
        return {status: 'error', error};
    }
}

// Add this new helper function:
async function determineStartupPath() {
    console.info("determineStartupPath()... starting");

    // 1. Check for Hardware SN (URL param or Chrome Extension environment)
    const urlSn = await getSnFromUrl();
    const isChromeExtension = typeof chrome !== 'undefined' && chrome.runtime;
    const hasConfigSn = !!import.meta.env.VITE_SERIAL_NUMBER;
    let decision

    if (urlSn || isChromeExtension || hasConfigSn) {
        console.info(urlSn)
        console.info(isChromeExtension)
        console.info(hasConfigSn)
        startupMode.value = 'hardware';
        decision = 'hardware';
    } else {
        const token = getPairingToken();
        const deviceId = getDeviceId();
        if (token && deviceId) {
            updateLoadingStatus('Validating pairing token...', 'init');

            decision = 'virtual-paired';
            startupMode.value = decision;
            systemInfo.serialNumber = deviceId
        } else {
            // No token, no SN. Needs pairing.
            decision = 'virtual-unpaired';
        }
    }

    console.info(`determineStartupPath()... done: ${decision}`);
    return decision
}


// Main startup sequence
export async function startupSequence1(statusCallback) {
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    setSystemTime();
    getIP();

    try {
        updateLoadingStatus('Getting serial number...', 'serialNumber');
        await getSerialNumber();

        updateLoadingStatus('Performing handshake...', 'handshake');
        await doHandshake();

        updateLoadingStatus('Connecting to Buzztime Remote...', 'remote');
        await doRemoteConnection();

        updateLoadingStatus('Fetching Apps...', 'apps');
        await getApps();

        updateLoadingStatus('Loading prize data...', 'prizeData');
        await getBarHeroStatus();

        updateLoadingStatus('Initialization Complete!', 'complete');

        await new Promise(resolve => setTimeout(resolve, params.startupWait * 1000));
        return true;
    } catch (error) {
        console.error('Startup sequence failed:', error);
        return false;
    }
}

// Get device serial number
async function getSerialNumber() {
    console.info("getSerialNumber()... starting");
    const key = 'serialNumber';

    try {
        // Check if URL param is set for SN
        const sn = await getSnFromUrl()
        if (sn) {
            systemInfo.serialNumber = sn
            return;
        }

        if (import.meta.env.VITE_SERIAL_NUMBER === '') {
            updateLoadingStatus('Detecting device serial number...', 'serialNumber');

            // For Chrome extension integration
            if (typeof chrome !== 'undefined' && chrome.runtime) {
                try {
                    const response = await new Promise((resolve, reject) => {
                        window.chrome.runtime.sendMessage(import.meta.env.VITE_CHROME_EXTENSION_ID, {action: "getSerialNumber"}, function (response) {
                            if (window.chrome.runtime.lastError) {
                                reject(window.chrome.runtime.lastError);
                            } else {
                                resolve(response);
                            }
                        });
                    });

                    if (response && response.text) {
                        systemInfo.serialNumber = response.text;
                        console.log("RESPONSE SN", response.text);
                        // logState.
                        clearFailure(key);
                        console.info("getSerialNumber()... DONE");
                        return systemInfo.serialNumber;
                    } else {
                        throw new Error("No serial number returned");
                    }
                } catch (e) {
                    console.error("getSerialNumber()... FAILED\n", e);
                    systemInfo.serialNumber = "error";
                    updateLoadingStatus('Unable to detect serial number. Retrying...', 'serialNumber', false);

                    return await doFailureSequence(key, getSerialNumber, "Console Error. Unable to detect SN");
                }
            } else {
                throw new Error("Chrome extension error");
                // For non-Chrome environments, use a mock serial number for development
                // systemInfo.serialNumber = import.meta.env.serialNumber
                // console.info("getSerialNumber()... DONE (mock)");
                // return systemInfo.serialNumber;
            }
        } else {
            // Use configured serial number
            systemInfo.serialNumber = import.meta.env.VITE_SERIAL_NUMBER;
            console.info("getSerialNumber()... DONE (config)");
            return systemInfo.serialNumber;
        }
    } catch (e) {
        console.error(`getSerialNumber()... FAILED\n${e}\n${e.stack}`);
        systemInfo.serialNumber = "error";
        updateLoadingStatus('Serial number detection failed. Retrying...', 'serialNumber', false);

        return await doFailureSequence(key, getSerialNumber, "Console Error. Unable to detect SN");
    }
}

// Perform handshake with server
async function doHandshake() {
    console.info("doHandshake()... starting");
    console.log("DEVICE SN", systemInfo.serialNumber);
    const key = 'handshake';

    try {
        updateLoadingStatus('Connecting to Buzztime network...', 'handshake');

        const handshakeResponse = await spectatorHandshake(systemInfo.serialNumber);

        systemInfo.handshakeInfo = {
            siteId: handshakeResponse.site_id,
            siteCity: handshakeResponse.site_city,
            siteName: handshakeResponse.site_name,
            siteState: handshakeResponse.site_state,
            siteZip: handshakeResponse.site_zip,
            handshakeSettings: handshakeResponse.settings,
            statusId: handshakeResponse.status_id,

            playerToken: null,
            rmq: null,
            siteType: 0,
        }

        systemInfo.siteId = handshakeResponse.site_id

        clearFailure(key);
        console.info("doHandshake()... DONE");
        updateLoadingStatus('Connected to Buzztime network', 'handshake');
    } catch (e) {
        console.error(`doHandshake()... FAILED\n${e}`);
        systemInfo.siteId = "error"
        updateLoadingStatus('Connection failed. Retrying...', 'handshake', false);

        return await doFailureSequence(key, doHandshake, "Console Error. Handshake failed");
    }
}

async function getApps() {
    console.info('getApps()... starting');
    const key = 'apps';

    try {
        if (!systemInfo.handshakeInfo || !systemInfo.siteId) await doHandshake();

        let fetchedApps = {apps: []};

        try {
            fetchedApps = await getAppsForDevice(systemInfo.serialNumber);
        } catch (error) {
            console.error('getAppsForDevice failed:', error);
            systemInfo.apps = fallbackAppsList;
        }

        await processApps(fetchedApps);
 
        initializeApps()
        initializeDefaultApp()
        ensureAppIds()

        clearFailure(key);
        console.info('getApps()... DONE');
    } catch (e) {
        console.error(`getApps()... FAILED\n${e}`);
        // Fallback to empty arrays
        systemInfo.apps = [];
        systemInfo.internalApps = [];
        systemInfo.betaApps = [];
        updateLoadingStatus('Fetching apps failed. Retrying...', 'apps', false);
        return await doFailureSequence(key, getApps, "Error. Apps fetch failed");
    }
}

let deviceManager = null;

window.addEventListener('beforeunload', () => {
    deviceManager?.destroy()
})

async function doRemoteConnection() {
    console.info("doRemoteConnection()... starting");
    const key = 'remote';
    const SITE_ID = systemInfo.handshakeInfo.siteId;
    const SITE_NAME = systemInfo.handshakeInfo.siteName;

    if (deviceManager) {
        deviceManager.destroy()
        deviceManager = null
    }

    try {
        if (!SITE_ID) await doHandshake();

        const SERIAL_NUMBER = systemInfo?.serialNumber;

        const API_BASE_URL = import.meta.env.VITE_REMOTE_API_URL
        const WS_BASE_URL = import.meta.env.VITE_REMOTE_WS_URL
        deviceManager = await initializeDeviceManager(SITE_ID, SITE_NAME, SERIAL_NUMBER, API_BASE_URL, WS_BASE_URL)

        console.info("doRemoteConnection()... DONE");
        systemInfo.remote = "Connected"
        updateLoadingStatus('Connected to Buzztime Remote', 'remote');
    } catch (e) {
        console.error(`doRemoteConnection()... FAILED\n${e}\n${e.stack}`);
        systemInfo.remote = "Error";
        systemInfo.remote = false;
        updateLoadingStatus('Remote Connection failed.', 'remote', false);

        // return await doFailureSequence(key, doRemoteConnection, "Connection Error - Buzztime Remote");
    }
}

async function getBarHeroStatus() {
    console.info('getBarHeroStatus()... starting')

    const status = await barHero_getSiteStatus(systemInfo.siteId)

    systemInfo.prizeData.data = status;
    systemInfo.prizeEnabled = !!status.active;

    console.info('getBarHeroStatus()... DONE')
}

// Handle failures and retries
async function doFailureSequence(key, fn, notif) {
    console.error('doFailureSequence() Started.');

    const storageData = getFailure(key);
    let retries = storageData.retries || 0;
    let reboots = storageData.reboots || 0;
    let rebootTime = storageData.rebootTime;
    let retryCount = storageData.retryCount || 0;

    // const baseInterval = 10000; // 10 seconds
    const maxBackoffFactor = 6; // Maximum backoff factor (10s * 2^6 = 640s)
    // const backoffFactor = Math.min(retryCount, maxBackoffFactor);
    // const backoffTime = baseInterval * Math.pow(2, backoffFactor);

    const backoffTime = 5000; // BTV-32. Hard 5s while ongoing login/ handshake issues persist

    if (!rebootTime) {
        rebootTime = Date.now() + Math.floor(Math.random() * (900000 - 600000 + 1)) + 600000; // 10-15 minutes
        storeFailure(key, 'time', rebootTime);
    }

    if (Date.now() >= rebootTime) {
        storeFailure(key, 'reboot');
        const updatedData = JSON.parse(localStorage.getItem(key) || '{}');
        delete updatedData.rebootTime;
        delete updatedData.retryCount;
        localStorage.setItem(key, JSON.stringify(updatedData));

        // window.location.reload();
        rebootDevice('startup')
        return;
    }

    storeFailure(key, 'retry');
    retryCount++;
    storeFailure(key, 'rc', retryCount);

    // Reboot after 10 failures
    if (retryCount >= 10) {
        rebootDevice();
        storeFailure(key, 'rc', 0);
    }

    const msg = `${notif} | Attempt ${retries + 1} | Retrying in ${Math.round(backoffTime / 1000)}s`;
    updateLoadingStatus(msg, key, false);

    console.log("retryTime", backoffTime);

    // Return a promise that resolves when the retry succeeds
    return new Promise((resolve, reject) => {
        setTimeout(async () => {
            try {
                const result = await fn();
                resolve(result);
            } catch (error) {
                reject(error);
            }
        }, backoffTime);
    });
}

// Helper functions for failure handling
function storeFailure(key, type, arg = null) {
    const storageData = JSON.parse(localStorage.getItem(key) || '{}');
    let retries = (storageData?.retries ?? 0);
    let reboots = (storageData?.reboots ?? 0);

    if (type === 'reboot') {
        reboots++;
        storageData.reboots = reboots;
    } else if (type === 'retry') {
        retries++;
        storageData.retries = retries;
    } else if (type === 'time') {
        storageData.rebootTime = arg;
    } else if (type === 'rc') {
        storageData.retryCount = arg;
    }

    localStorage.setItem(key, JSON.stringify(storageData));
}

function clearFailure(key) {
    localStorage.setItem(key, '{}');
}

function getFailure(key) {
    return JSON.parse(localStorage.getItem(key) || '{}');
}
